import { useEffect, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ConfirmModal } from "../components/ConfirmModal";
import { Shell } from "../components/Shell";
import { ApiError, tenants as tenantsApi } from "../lib/api";
import type { SigningStatus, Tenant } from "../lib/api";

function fmt(iso: string | null): string {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString("es-CO", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  } catch {
    return iso;
  }
}

export function TenantSigningPage() {
  const { id } = useParams<{ id: string }>();
  const [tenant, setTenant] = useState<Tenant | null>(null);
  const [status, setStatus] = useState<SigningStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [ok, setOk] = useState<string | null>(null);
  const [password, setPassword] = useState("");
  const [uploading, setUploading] = useState(false);
  const [showRemoveCert, setShowRemoveCert] = useState(false);
  const [removeBusy, setRemoveBusy] = useState(false);
  const fileRef = useRef<HTMLInputElement | null>(null);

  const load = async () => {
    if (!id) return;
    setLoading(true);
    try {
      const [t, s] = await Promise.all([
        tenantsApi.get(id),
        tenantsApi.getSigning(id),
      ]);
      setTenant(t);
      setStatus(s);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Error de red");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const upload = async () => {
    if (!id) return;
    const file = fileRef.current?.files?.[0];
    if (!file) {
      setError("Selecciona un archivo .p12 / .pfx");
      return;
    }
    if (!password) {
      setError("Ingresa la contraseña del certificado");
      return;
    }
    setUploading(true);
    setError(null);
    setOk(null);
    try {
      const s = await tenantsApi.uploadSigningCert(id, file, password);
      setStatus(s);
      setOk("Certificado cargado correctamente.");
      setPassword("");
      if (fileRef.current) fileRef.current.value = "";
      setTimeout(() => setOk(null), 2400);
    } catch (err) {
      setError(
        err instanceof ApiError
          ? typeof err.detail === "string"
            ? err.detail
            : err.message
          : "Error de red",
      );
    } finally {
      setUploading(false);
    }
  };

  const toggle = async (enabled: boolean) => {
    if (!id) return;
    setError(null);
    setOk(null);
    try {
      const s = await tenantsApi.toggleSigning(id, enabled);
      setStatus(s);
    } catch (err) {
      setError(
        err instanceof ApiError
          ? typeof err.detail === "string"
            ? err.detail
            : err.message
          : "Error de red",
      );
    }
  };

  const removeCert = async () => {
    if (!id) return;
    setRemoveBusy(true);
    try {
      const s = await tenantsApi.deleteSigningCert(id);
      setStatus(s);
      setShowRemoveCert(false);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Error de red");
    } finally {
      setRemoveBusy(false);
    }
  };

  if (loading) {
    return (
      <Shell>
        <section className="page">
          <div className="eyebrow">Cargando…</div>
        </section>
      </Shell>
    );
  }

  return (
    <Shell>
      <section className="page">
        <div style={{ marginBottom: 24 }}>
          <div className="eyebrow">
            <Link to="/tenants">Alcaldías</Link>
            {tenant && (
              <>
                {" · "}
                <Link to={`/tenants/${tenant.id}`}>{tenant.nombre}</Link>
              </>
            )}
            {" · Firma digital"}
          </div>
          <h1>Firma electrónica de PDFs</h1>
          <p className="lede">
            Sube un certificado PKCS#12 emitido por una entidad reconocida
            (Andes SCD, Certicámara, GSE…) para que los PDFs de respuesta
            oficial se firmen automáticamente en formato PAdES.
          </p>
        </div>

        {error && <div className="alert err">{error}</div>}
        {ok && <div className="alert ok">{ok}</div>}

        <div className="card" style={{ marginBottom: 20 }}>
          <div className="card-inner">
            <div className="eyebrow">Estado actual</div>
            <table style={{ width: "100%", marginTop: 8 }}>
              <tbody>
                <Row label="Certificado cargado" value={status?.has_cert ? "Sí" : "No"} />
                <Row
                  label="Firma activa"
                  value={status?.enabled ? "Sí — los PDFs se firman" : "No"}
                />
                <Row
                  label="Sujeto del certificado"
                  value={status?.subject ?? "—"}
                  mono
                />
                <Row label="Válido hasta" value={fmt(status?.valid_until ?? null)} />
              </tbody>
            </table>

            {status?.has_cert && (
              <div style={{ display: "flex", gap: 10, marginTop: 20 }}>
                <button
                  type="button"
                  className="btn-outline"
                  onClick={() => void toggle(!status.enabled)}
                >
                  {status.enabled ? "Desactivar firma" : "Activar firma"}
                </button>
                <button
                  type="button"
                  className="btn-outline"
                  style={{ color: "var(--err)" }}
                  onClick={() => setShowRemoveCert(true)}
                >
                  Eliminar certificado
                </button>
              </div>
            )}
          </div>
        </div>

        <div className="card">
          <div className="card-inner">
            <div className="eyebrow">
              {status?.has_cert ? "Reemplazar certificado" : "Subir certificado"}
            </div>
            <p style={{ color: "var(--ink-3)", marginTop: 4, marginBottom: 16 }}>
              Archivo en formato PKCS#12 (.p12 o .pfx), máximo 256 KB. La
              contraseña se almacena cifrada con la KEK global de plataforma.
            </p>

            <div className="form-row">
              <label className="label">Archivo .p12 / .pfx</label>
              <input
                ref={fileRef}
                type="file"
                accept=".p12,.pfx"
                className="input"
              />
            </div>
            <div className="form-row">
              <label className="label">Contraseña del .p12</label>
              <input
                type="password"
                className="input"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                autoComplete="off"
              />
            </div>

            <div style={{ display: "flex", gap: 10, marginTop: 20 }}>
              <button
                type="button"
                className="cta"
                style={{ maxWidth: 260, width: "auto" }}
                disabled={uploading}
                onClick={() => void upload()}
              >
                <span className="cta-inner">
                  {uploading ? "Subiendo…" : "Subir certificado"}
                </span>
              </button>
            </div>
          </div>
        </div>
      </section>

      <ConfirmModal
        open={showRemoveCert}
        title="¿Eliminar el certificado del tenant?"
        message="Se borrará el .p12 y la frase de paso del servidor. La firma PAdES quedará desactivada hasta que vuelvas a subir un certificado."
        confirmLabel="Sí, eliminar"
        variant="danger"
        busy={removeBusy}
        onCancel={() => setShowRemoveCert(false)}
        onConfirm={() => void removeCert()}
      />
    </Shell>
  );
}

function Row({
  label,
  value,
  mono,
}: {
  label: string;
  value: string;
  mono?: boolean;
}) {
  return (
    <tr style={{ borderBottom: "1px solid var(--hairline)" }}>
      <td
        style={{
          padding: "10px 12px",
          color: "var(--ink-3)",
          fontFamily: "var(--font-mono)",
          fontSize: 12,
          letterSpacing: "0.14em",
          textTransform: "uppercase",
          width: 220,
        }}
      >
        {label}
      </td>
      <td
        style={{
          padding: "10px 12px",
          fontSize: mono ? 13 : 14,
          fontFamily: mono ? "var(--font-mono)" : "var(--font-app)",
        }}
      >
        {value}
      </td>
    </tr>
  );
}
